/**
 * Folding the design tier (`widget.appearance`) into the `[data]` object the
 * shared chart components actually read.
 *
 * Without this the inspector writes `appearance` and nothing ever looks at it —
 * the chart keeps rendering whatever the sample generator produced.
 *
 * ## Why the keys line up
 *
 * The unified charts put **everything** in `data.capabilities` — there is no
 * separate format channel — and 48 of the 56 format-control keys in
 * `dvm-layout-builder.json` + `charts/bar-column.json` are already named
 * exactly like `BarColumnCapabilities` fields (`showLegend`, `palette`,
 * `labelMode`, `tooltipTrigger`, `barWidthPercent`, …). So the merge is a
 * straight overlay, not a translation table.
 *
 * The eight that do NOT map are the card chrome — see
 * {@link NODE_LEVEL_FORMAT_KEYS}.
 */

import { normalizeTableCellConfig, type TableCellConfig } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import type { WidgetContent } from '../design-builder.types';
import { ensureGoogleFonts } from '../../../shared-services/google-font-loader';
import { appearanceSpecFor } from './design-appearance.spec';
import { sampleWidgetData, usesSeries } from './design-sample-data';

/** Appearance key holding the Design tab's per-column cell configuration
 *  (Table & Matrix "Columns" section): column key → TableCellConfig. It is
 *  NOT a capability — it is folded into `capabilities.columns[].cellConfig`,
 *  `capabilities.measures[].cellConfig` and the baked drill-down headers. */
export const COLUMN_CELL_CONFIGS_KEY = 'columnCellConfigs';

/**
 * Format keys that describe the CARD around a chart, not the chart.
 *
 * In the DVM Layout Builder a tile owns its own card, so these make sense
 * there. In the Design Builder the card IS the node — its fill, corner radius,
 * stroke and shadow are already edited in the inspector's Appearance / Stroke /
 * Shadow sections. Showing them twice would give two controls fighting over the
 * same pixels, so they are filtered out of the Format list and never merged
 * into `capabilities`.
 */
export const NODE_LEVEL_FORMAT_KEYS: ReadonlySet<string> = new Set([
  'cardBackground',
  'cardBorderColor',
  'cardBorderWidth',
  'cardCornerRadius',
  'cardHeaderBackground',
  'cardHeaderColor',
  'cardTransparent',
  'titleAlign',
]);

/**
 * Appearance minus the keys the node's own style already covers, with the
 * variant renamed to whatever capability field this family actually reads.
 */
export function chartAppearance(
  appearance: Record<string, unknown> | undefined,
  layoutType = '',
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const variantKey = appearanceSpecFor(layoutType).variantCapabilityKey ?? 'variant';

  for (const [key, value] of Object.entries(appearance ?? {})) {
    // Ignore legacy label-deletion visibility state in saved designs.
    if (key === 'hideControlAfterLabelDeletion') continue;
    if (NODE_LEVEL_FORMAT_KEYS.has(key) || key === COLUMN_CELL_CONFIGS_KEY) continue;
    // An empty string is how a select says "default" — writing it through would
    // override a real default with nothing.
    if (value === '' || value === null || value === undefined) continue;
    // Canvas charts draw their text off-page, so load a picked Google font here.
    if (typeof value === 'string' && /fontfamily/i.test(key)) ensureGoogleFonts(value);
    out[key === 'variant' ? variantKey : key] = value;
  }
  return out;
}

/**
 * The `[data]` object to hand the shared component: the widget's data with the
 * design tier overlaid.
 *
 * Two shapes exist among the shared components and both are handled:
 * - **capability-driven** (`BAR`, `LINE_AREA`, `SCATTER_BUBBLE`, `PIE_COMPOSITION`,
 *   `TABLE_MATRIX`) — everything goes under `capabilities`;
 * - **flat** (`KPI`, `WATERFALL`, `HORIZONTAL_BAR_CHART`, the input controls) —
 *   settings sit at the top level.
 *
 * Presence of a `capabilities` object decides which, so a component that gains
 * capabilities later needs no change here.
 */
export function resolveWidgetData(
  widget: WidgetContent | null | undefined,
): Record<string, unknown> | null {
  if (!widget?.data) return null;

  const data = applyColumnCellConfigs(currentTableSample(widget) ?? widget.data, widget.appearance?.[COLUMN_CELL_CONFIGS_KEY]);
  const overlay = chartAppearance(widget.appearance, widget.layoutType);
  const label = widget.appearance?.['label'];
  if (['DROPDOWN', 'INPUT_BOX', 'SLIDER'].includes(widget.layoutType) && typeof label === 'string') {
    // Blank labels are intentional; do not fall back to sample or dataset names.
    overlay['label'] = label.trim();
    if (widget.layoutType === 'DROPDOWN') overlay['name'] = label.trim();
    if (!label.trim()) overlay['showLabel'] = false;
  }
  if (!Object.keys(overlay).length) return data;

  const capabilities = data['capabilities'];

  if (capabilities && typeof capabilities === 'object' && !Array.isArray(capabilities)) {
    const merged = { ...(capabilities as Record<string, unknown>), ...overlay };
    return { ...data, capabilities: stripStaleSeries(merged, widget) };
  }

  return { ...data, ...overlay };
}

/**
 * Fold the Design tab's per-column cell configuration into a Table & Matrix
 * widget's data: `capabilities.columns[].cellConfig` (created from the rows'
 * keys when the table infers its columns), `capabilities.measures[].cellConfig`
 * and the baked drill-down headers' `cellConfig` — the same places the Layout
 * Builder writes them, so the renderers need no Design-Builder branch. An
 * editor declared on a drill-down header also marks it editable, exactly as
 * the shared widget builder would. Returns the SAME object when nothing applies.
 */
export function applyColumnCellConfigs(
  data: Record<string, unknown>,
  raw: unknown,
): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return data;
  const configs = new Map<string, TableCellConfig>();
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const cfg = normalizeTableCellConfig(value);
    // An explicit empty Design override clears an inherited cell configuration.
    if (!key || !value || typeof value !== 'object' || Array.isArray(value)) return;
    const plainText = (value as TableCellConfig).display === 'text';
    if (cfg || plainText || !Object.keys(value).length) configs.set(key, plainText ? { ...cfg, display: 'text' } : cfg ?? {});
  });
  if (!configs.size) return data;
  const norm = (s: unknown): string => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const byNorm = new Map<string, TableCellConfig>();
  configs.forEach((cfg, key) => byNorm.set(norm(key), cfg));
  const lookup = (...keys: unknown[]): TableCellConfig | undefined => {
    for (const key of keys) {
      if (key === undefined || key === null || key === '') continue;
      const hit = configs.get(String(key)) ?? byNorm.get(norm(key));
      if (hit) return hit;
    }
    return undefined;
  };

  const next: Record<string, unknown> = { ...data };
  const caps = { ...((data['capabilities'] ?? {}) as Record<string, unknown>) };
  let touched = false;

  // Flat columns — configured list, else the keys the table would infer.
  const configured = Array.isArray(caps['columns']) ? (caps['columns'] as Array<Record<string, unknown>>) : [];
  const rows = (Array.isArray(data['staticData']) ? data['staticData'] : Array.isArray(data['rows']) ? data['rows'] : []) as Array<Record<string, unknown>>;
  if (configured.length) {
    caps['columns'] = configured.map((column) => {
      const found = column && !column['isAction'] ? lookup(column['field'], column['label']) : undefined;
      if (!found) return column;
      // The Data tab's "Convert currency" tick survives a Design override that does not set it.
      const own = column['cellConfig'] as TableCellConfig | undefined;
      const cfg = found.convertCurrency === undefined && own?.convertCurrency ? { ...found, convertCurrency: true } : found;
      return { ...column, cellConfig: cfg,
        ...(cfg.display === 'link' ? { control: 'hyperlink', hrefTemplate: cfg.linkTemplate } : {}),
        ...(cfg.display && cfg.display !== 'link' && column['control'] === 'hyperlink' ? { editable: false } : {}),
      };
    });
    touched = true;
  } else if (rows[0] && !caps['dimensions'] && !data['drilldownData']) {
    const keys = Object.keys(rows[0]).filter((key) => !key.startsWith('__'));
    if (keys.some((key) => lookup(key))) {
      caps['columns'] = keys.map((field) => {
        const cfg = lookup(field);
        return cfg ? { field, cellConfig: cfg, ...(cfg.display === 'link' ? { control: 'hyperlink' } : {}) } : { field };
      });
      touched = true;
    }
  }

  // Drill-down measures (recompute path) + the baked headers (render path).
  const measures = Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>) : [];
  if (measures.length) {
    caps['measures'] = measures.map((measure) => {
      const cfg = lookup(measure['label'], measure['field']);
      return cfg ? { ...measure, cellConfig: cfg } : measure;
    });
    touched = true;
  }
  const patchHeaders = (drill: Record<string, unknown>): Record<string, unknown> => {
    const headers = Array.isArray(drill['headers']) ? (drill['headers'] as Array<Record<string, unknown>>) : [];
    if (!headers.length) return drill;
    return {
      ...drill,
      headers: headers.map((header) => {
        if (!header || header['expandabile']) return header;
        const cfg = lookup(header['prop'], header['fullName'], header['name']);
        if (!cfg) return header;
        const editor = cfg.editor && cfg.editor !== 'none' ? cfg.editor : undefined;
        return {
          ...header,
          cellConfig: cfg,
          ...(editor ? { editable: true } : {}),
          ...(editor === 'dropdown' ? { editor: 'dropdown', options: cfg.options ?? [], optionsFromData: cfg.optionsFromData === true } : {}),
        };
      }),
    };
  };
  if (data['drilldownData'] && typeof data['drilldownData'] === 'object') {
    next['drilldownData'] = patchHeaders(data['drilldownData'] as Record<string, unknown>);
    touched = true;
  }
  if (caps['drilldownData'] && typeof caps['drilldownData'] === 'object') {
    caps['drilldownData'] = patchHeaders(caps['drilldownData'] as Record<string, unknown>);
    touched = true;
  }
  // A cell editor on a flat column turns the Normal table's cell editing on
  // (the Layout Builder sets this flag from its per-column Editable toggle).
  const flatColumns = Array.isArray(caps['columns']) ? (caps['columns'] as Array<Record<string, unknown>>) : [];
  if (caps['tableType'] !== 'editable' && flatColumns.some((column) => {
    const cfg = column['cellConfig'] as TableCellConfig | undefined;
    return !!cfg?.editor && cfg.editor !== 'none';
  })) {
    caps['cellEditing'] = true;
  }
  if (!touched) return data;
  next['capabilities'] = caps;
  return next;
}

const SAMPLE_TABLE_LAYOUTS: ReadonlySet<string> = new Set(['TABLE_MATRIX', 'DRILL_DOWN_TABLE', 'NESTED_TABLE']);

/**
 * Sample tables are rebuilt from the generator on every render. Their data is
 * deterministic per node, so nothing visible changes — except that designs
 * saved with an older, wrongly-shaped sample (flat drill-down rows, a table
 * type with no matching fields) render correctly instead of "No data".
 */
function currentTableSample(widget: WidgetContent): Record<string, unknown> | null {
  if (!widget.sample || !SAMPLE_TABLE_LAYOUTS.has(widget.layoutType)) return null;
  const nodeId = String(widget.data?.['widgetId'] ?? '').replace(/^dsg-/, '');
  if (!nodeId) return null;
  return sampleWidgetData(widget.layoutType, nodeId, String(widget.data?.['title'] ?? ''), widget.appearance ?? {});
}

/**
 * A single-series variant must not keep a `series` split.
 *
 * The overlay can only ADD keys, so switching from Grouped to Simple left the
 * old `series` in place: the chart was told `variant: 'simple'` while still
 * being handed a split, and kept rendering grouped bars. Removing it here fixes
 * that for bound data too, not just for regenerated sample data.
 */
function stripStaleSeries(
  capabilities: Record<string, unknown>,
  widget: WidgetContent,
): Record<string, unknown> {
  if (!('series' in capabilities)) return capabilities;

  const variant = String(capabilities['variant'] ?? widget.appearance?.['variant'] ?? '');
  if (!variant || usesSeries(variant)) return capabilities;

  const { series, ...rest } = capabilities;
  return rest;
}
